import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Crosshair, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <div className="flex-1 hidden lg:flex flex-col justify-between p-12 border-r border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 diag-stripes opacity-60 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src="/rd-logo.jpeg" alt="" className="w-10 h-10 rounded-md object-cover" />
            <span className="font-heading text-2xl font-black tracking-tight">RD <span className="text-[#FF3B30]">SmartMetrics</span></span>
          </div>
        </div>
        <div className="relative max-w-md">
          <div className="overline mb-4">// Performance Intelligence</div>
          <h1 className="font-heading text-6xl font-black leading-[0.92] uppercase">
            Train.<br/>
            Test.<br/>
            <span className="text-[#FF3B30]">Dominate.</span>
          </h1>
          <p className="mt-6 text-[#A1A1AA] text-sm max-w-sm leading-relaxed">
            A tactical dashboard for athletic testing — speed, jump, strength, and quadrant profiling, calibrated for elite progression.
          </p>
        </div>
        <div className="relative overline">// v1.0 / 2026</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline mb-2">// Access Terminal</div>
          <h2 className="font-heading text-4xl font-black uppercase mb-8">Sign In</h2>

          {err && (
            <div className="mb-4 px-3 py-2 border border-[#FF3B30]/40 text-[#FF3B30] text-sm" data-testid="login-error">{err}</div>
          )}

          <label className="block mb-5">
            <span className="overline block mb-2">Email</span>
            <input
              data-testid="login-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-b border-white/20 py-3 text-base text-white focus:border-[#FF3B30] transition-colors"
            />
          </label>

          <label className="block mb-8">
            <span className="overline block mb-2">Password</span>
            <input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b border-white/20 py-3 text-base text-white focus:border-[#FF3B30] transition-colors"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full bg-[#FF3B30] hover:bg-[#D92B22] active:bg-[#B91C16] text-white font-bold uppercase tracking-[0.18em] py-4 min-h-[48px] text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 rounded"
          >
            {busy ? "Authenticating…" : <>Enter <ArrowRight size={14} /></>}
          </button>

          <div className="mt-4 text-xs text-right">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-[#A1A1AA] hover:text-white transition-colors">Forgot password?</Link>
          </div>

          <div className="mt-6 text-xs text-[#A1A1AA]">
            New athlete?{" "}
            <Link to="/register" data-testid="goto-register" className="text-white underline">Create account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
